/**
 * Centralized Google Maps configuration and validation utility.
 * Prevents duplicate loads, ensures consistent parameters, and provides secure key handling.
 */

// In-memory dynamic values retrieved from the backend config endpoint at runtime
let dynamicApiKey = "";
let dynamicMapId = "";

/**
 * Checks for a compile-time Vite environment variable first, then falls back to in-memory runtime values.
 */
export function getGoogleMapsApiKey(): string {
  // If we have a runtime fetched key from the backend, ALWAYS prioritize it!
  if (dynamicApiKey && dynamicApiKey.trim() !== "") {
    return dynamicApiKey;
  }
  // @ts-ignore
  const envKey = import.meta.env.VITE_GOOGLE_MAPS_PLATFORM_KEY;
  if (envKey && envKey !== "YOUR_API_KEY" && envKey !== "MY_GOOGLE_MAPS_PLATFORM_KEY") {
    return envKey;
  }
  // Try fallback to process.env if injected via build-time define block
  try {
    const processEnvKey = (process as any).env?.GOOGLE_MAPS_PLATFORM_KEY || (process as any).env?.VITE_GOOGLE_MAPS_PLATFORM_KEY;
    if (processEnvKey && processEnvKey !== "YOUR_API_KEY" && processEnvKey !== "MY_GOOGLE_MAPS_PLATFORM_KEY") {
      return processEnvKey;
    }
  } catch (e) {
    // process.env may not be defined in all client environments
  }
  return dynamicApiKey;
}

/**
 * Checks for a compile-time Vite map ID, falling back to in-memory runtime values.
 */
export function getGoogleMapsMapId(): string {
  // If we have a runtime fetched map ID from the backend, ALWAYS prioritize it!
  if (dynamicMapId && dynamicMapId.trim() !== "") {
    return dynamicMapId;
  }
  // @ts-ignore
  const envMapId = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID;
  if (envMapId) {
    return envMapId;
  }
  try {
    const processEnvMapId = (process as any).env?.GOOGLE_MAPS_MAP_ID || (process as any).env?.VITE_GOOGLE_MAPS_MAP_ID;
    if (processEnvMapId) {
      return processEnvMapId;
    }
  } catch (e) {
    // process.env may not be defined in all client environments
  }
  return dynamicMapId;
}

/**
 * Updates the runtime-fetched dynamic Google Maps configurations.
 */
export function setDynamicGoogleMapsConfig(key: string, mapId: string) {
  if (key) {
    dynamicApiKey = key;
  }
  if (mapId) {
    dynamicMapId = mapId;
  }
}

/**
 * Helper to validate if a key exists and is non-placeholder.
 */
export function hasGoogleMapsApiKey(): boolean {
  const key = getGoogleMapsApiKey();
  return Boolean(key) && key !== "YOUR_API_KEY" && key !== "MY_GOOGLE_MAPS_PLATFORM_KEY" && key.trim() !== "";
}

/**
 * Dynamic configuration object with ES6 getters.
 * This guarantees components importing this object will always access the latest values.
 */
export const googleMapsConfig = {
  get apiKey(): string {
    return getGoogleMapsApiKey();
  },
  get mapId(): string {
    return getGoogleMapsMapId();
  },
  get hasKey(): boolean {
    return hasGoogleMapsApiKey();
  }
};


import { logger } from "../utils/debugLogger";

/**
 * Logs a masked version of the loaded key for secure debugging.
 */
export function logSafeConfig() {
  const key = getGoogleMapsApiKey();
  const mapId = getGoogleMapsMapId();
  const maskedKey = key ? `${key.substring(0, 6)}...${key.substring(Math.max(0, key.length - 4))}` : "none";
  logger.debug(`[Google Maps Config] Masked Key: ${maskedKey}, Map ID: ${mapId || "none"}, Valid Key: ${hasGoogleMapsApiKey()}`);
}

/**
 * Returns a friendly message if the key appears to have formatting issues or placeholders.
 */
export function validateGoogleMapsKey(): { valid: boolean; warning?: string } {
  const key = getGoogleMapsApiKey();
  if (!key || key.trim() === "") {
    return { valid: false, warning: "Google Maps API Key is missing. Please check your configuration." };
  }
  if (key === "YOUR_API_KEY" || key === "MY_GOOGLE_MAPS_PLATFORM_KEY") {
    return { valid: false, warning: "You are using a placeholder API Key. A real Google Maps API Key is required." };
  }
  if (!key.startsWith("AIza")) {
    return { valid: false, warning: "The API key does not start with 'AIza', which is characteristic of Google Maps API keys." };
  }
  return { valid: true };
}
